import { z } from "zod";

// ============================================================================
// Query Keys
// ============================================================================

export const DATABRICKS_QUERY_KEYS = {
  CONNECTIONS: "databricks-connections",
  CONNECTION: "databricks-connection",
} as const;

// ============================================================================
// Constants
// ============================================================================

export const DatabricksAuthType = {
  PAT: "pat",
  OAUTH_M2M: "oauth_m2m",
  AZURE_SP: "azure_sp",
} as const;
export type DatabricksAuthTypeValue = (typeof DatabricksAuthType)[keyof typeof DatabricksAuthType];

// ============================================================================
// Shared Auth Schemas (internal building blocks)
// ============================================================================

const DatabricksPATAuthSchema = z.object({
  auth_type: z.literal(DatabricksAuthType.PAT),
  access_token: z.string().min(1, "Access token is required"),
});

const DatabricksOAuthM2MAuthSchema = z.object({
  auth_type: z.literal(DatabricksAuthType.OAUTH_M2M),
  client_id: z.string().min(1, "Client ID is required"),
  client_secret: z.string().min(1, "Client Secret is required"),
});

const DatabricksAzureSPAuthSchema = z.object({
  auth_type: z.literal(DatabricksAuthType.AZURE_SP),
  azure_tenant_id: z.string().min(1, "Tenant ID is required"),
  client_id: z.string().min(1, "Client ID is required"),
  client_secret: z.string().min(1, "Client Secret is required"),
});

// ============================================================================
// API Response Schemas
// ============================================================================

export const DatabricksConnectionResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  server_hostname: z.string(),
  http_path: z.string(),
  auth_type: z.enum(["pat", "oauth_m2m", "azure_sp"]).default("pat"),
  workspace_id: z.string().nullable().optional(),
  metastore_id: z.string().nullable().optional(),
  is_valid: z.boolean().optional(),
  last_validated_at: z.string().nullable().optional(),
  // BE returns these as non-nullable datetimes — tighten the FE schema
  // so a missing field surfaces as a parse error instead of silently
  // becoming `null` in callers (PR #2155 review N15).
  created_at: z.string(),
  last_modified_at: z.string(),
  created_by: z.number(),
  last_modified_by: z.number(),
});
export type DatabricksConnectionResponse = z.infer<typeof DatabricksConnectionResponseSchema>;

export const DatabricksConnectionListResponseSchema = z.array(DatabricksConnectionResponseSchema);
export type DatabricksConnectionListResponse = z.infer<
  typeof DatabricksConnectionListResponseSchema
>;

export const DatabricksConnectionTestResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string().nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  catalogs: z.array(z.string()).nullable().optional(),
  current_catalog: z.string().nullable().optional(),
  metastore_id: z.string().nullable().optional(),
});
export type DatabricksConnectionTestResponse = z.infer<
  typeof DatabricksConnectionTestResponseSchema
>;

// ============================================================================
// API Request Schemas
// ============================================================================

const DatabricksConnectionBaseSchema = z.object({
  name: z.string().min(1, "Name is required").max(128),
  description: z.string().optional(),
  server_hostname: z.string().min(1, "Server hostname is required"),
  http_path: z.string().min(1, "HTTP path is required"),
});

export const DatabricksConnectionCreateRequestSchema = z.discriminatedUnion("auth_type", [
  DatabricksConnectionBaseSchema.merge(DatabricksPATAuthSchema),
  DatabricksConnectionBaseSchema.merge(DatabricksOAuthM2MAuthSchema),
  DatabricksConnectionBaseSchema.merge(DatabricksAzureSPAuthSchema),
]);
export type DatabricksConnectionCreateRequest = z.infer<
  typeof DatabricksConnectionCreateRequestSchema
>;

const DatabricksTestBaseSchema = z.object({
  server_hostname: z.string().min(1, "Server hostname is required"),
  http_path: z.string().min(1, "HTTP path is required"),
});

export const DatabricksConnectionTestRequestSchema = z.discriminatedUnion("auth_type", [
  DatabricksTestBaseSchema.merge(DatabricksPATAuthSchema),
  DatabricksTestBaseSchema.merge(DatabricksOAuthM2MAuthSchema),
  DatabricksTestBaseSchema.merge(DatabricksAzureSPAuthSchema),
]);
export type DatabricksConnectionTestRequest = z.infer<typeof DatabricksConnectionTestRequestSchema>;
